(function(){
  'use strict';

  var cleanups = new WeakMap();

  function listen(target, type, handler, options, bag) {
    if (!target) return;
    target.addEventListener(type, handler, options || false);
    bag.push(function(){ target.removeEventListener(type, handler, options || false); });
  }

  function watchMedia(query, handler, bag) {
    var media = window.matchMedia(query);
    if (media.addEventListener) {
      media.addEventListener('change', handler);
      bag.push(function(){ media.removeEventListener('change', handler); });
    } else if (media.addListener) {
      media.addListener(handler);
      bag.push(function(){ media.removeListener(handler); });
    }
    return media;
  }

  function watchIntersection(root, handler, bag, options) {
    if (!('IntersectionObserver' in window)) return null;
    var observer = new IntersectionObserver(handler, options || { threshold: 0.05 });
    observer.observe(root);
    bag.push(function(){ observer.disconnect(); });
    return observer;
  }

  function initialiseGallery(root) {
    if (!root || root.dataset.kikiCareGalleryReady === 'true') return;
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-cg-slide]'));
    if (!slides.length) return;
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-cg-dot]'));
    var previous = root.querySelector('[data-cg-prev]');
    var next = root.querySelector('[data-cg-next]');
    var bag = [];
    var desktop = watchMedia('(min-width: 990px)', syncMode, bag);
    var reduced = watchMedia('(prefers-reduced-motion: reduce)', restart, bag);
    var index = 0;
    var timer = null;
    var paused = false;
    var visible = true;

    root.dataset.kikiCareGalleryReady = 'true';
    root.setAttribute('role', 'region');
    root.setAttribute('aria-roledescription', 'carousel');
    root.setAttribute('aria-label', 'A gentle welcome home');
    slides.forEach(function(slide, i){
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', (i + 1) + ' of ' + slides.length);
    });
    var progress = root.querySelector('.cg-progress');
    if (progress) {
      progress.setAttribute('role', 'tablist');
      progress.setAttribute('aria-label', 'Choose a gallery image');
    }
    dots.forEach(function(dot, i){
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-controls', slides[i] ? slides[i].id || ('care-gallery-slide-' + i) : '');
      if (slides[i]) slides[i].id = slides[i].id || ('care-gallery-slide-' + i);
    });

    function stop() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function start() {
      stop();
      if (reduced.matches || paused || !visible || desktop.matches || slides.length < 2) return;
      timer = window.setInterval(function(){
        index = (index + 1) % slides.length;
        render();
      }, 5600);
    }

    function restart() {
      stop();
      start();
    }

    function render() {
      var mosaic = desktop.matches;
      slides.forEach(function(slide, i){
        var active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', mosaic || active ? 'false' : 'true');
        if ('inert' in slide) slide.inert = !mosaic && !active;
      });
      dots.forEach(function(dot, i){
        var active = i === index;
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
        dot.setAttribute('aria-current', active ? 'true' : 'false');
        dot.tabIndex = active ? 0 : -1;
      });
    }

    function go(to) {
      if (desktop.matches) return;
      index = (to + slides.length) % slides.length;
      render();
      restart();
    }

    function syncMode() {
      stop();
      render();
      start();
    }

    listen(previous, 'click', function(){ go(index - 1); }, false, bag);
    listen(next, 'click', function(){ go(index + 1); }, false, bag);
    dots.forEach(function(dot){
      listen(dot, 'click', function(){ go(Number(dot.dataset.cgDot) || 0); }, false, bag);
      listen(dot, 'keydown', function(event){
        var target = index;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') target = (index + 1) % slides.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') target = (index - 1 + slides.length) % slides.length;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = slides.length - 1;
        else return;
        event.preventDefault();
        go(target);
        dots[target].focus();
      }, false, bag);
    });
    listen(root, 'mouseenter', function(){ paused = true; stop(); }, false, bag);
    listen(root, 'mouseleave', function(){ paused = false; start(); }, false, bag);
    listen(root, 'focusin', function(){ paused = true; stop(); }, false, bag);
    listen(root, 'focusout', function(event){
      if (!root.contains(event.relatedTarget)) {
        paused = false;
        start();
      }
    }, false, bag);
    listen(document, 'visibilitychange', function(){
      if (document.hidden) stop();
      else start();
    }, false, bag);
    watchIntersection(root, function(entries){
      visible = Boolean(entries[0] && entries[0].isIntersecting);
      if (visible) start();
      else stop();
    }, bag, { threshold: 0.05 });
    syncMode();

    cleanups.set(root, function(){
      bag.forEach(function(cleanup){ cleanup(); });
      root.removeAttribute('data-kiki-care-gallery-ready');
    });
  }

  function initialiseArrival(root) {
    if (!root || root.dataset.kikiArrivalReady === 'true') return;
    var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-ar-step]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-ar-panel]'));
    if (!buttons.length || !panels.length) return;
    var videos = Array.prototype.slice.call(root.querySelectorAll('[data-ar-video]'));
    var bag = [];
    var current = 0;
    root.dataset.kikiArrivalReady = 'true';

    buttons.forEach(function(button, i){
      button.id = button.id || ('arrival-step-' + i);
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', 'arrival-panel-' + i);
    });
    panels.forEach(function(panel, i){
      panel.id = panel.id || ('arrival-panel-' + i);
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', buttons[i] ? buttons[i].id : '');
    });

    function prepare(video) {
      if (!video) return;
      var changed = false;
      if (video.dataset.poster) {
        video.poster = video.dataset.poster;
        video.removeAttribute('data-poster');
      }
      Array.prototype.slice.call(video.querySelectorAll('source[data-src]')).forEach(function(source){
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
        changed = true;
      });
      video.preload = 'metadata';
      if (changed || video.readyState === 0) video.load();
    }

    function select(step, shouldFocus) {
      current = Math.max(0, Math.min(step, buttons.length - 1));
      buttons.forEach(function(button, i){
        var active = i === current;
        button.setAttribute('aria-selected', active ? 'true' : 'false');
        button.tabIndex = active ? 0 : -1;
      });
      panels.forEach(function(panel, i){
        var active = i === current;
        panel.hidden = !active;
        panel.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (active) prepare(panel.querySelector('[data-ar-video]'));
      });
      videos.forEach(function(video){
        var owner = video.closest('[data-ar-panel]');
        if (!owner || owner.id !== panels[current].id) video.pause();
      });
      if (shouldFocus) buttons[current].focus();
    }

    buttons.forEach(function(button, i){
      listen(button, 'click', function(){ select(i, false); }, false, bag);
      listen(button, 'keydown', function(event){
        var target = i;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') target = (i + 1) % buttons.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') target = (i - 1 + buttons.length) % buttons.length;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = buttons.length - 1;
        else return;
        event.preventDefault();
        select(target, true);
      }, false, bag);
    });
    listen(document, 'visibilitychange', function(){
      if (document.hidden) videos.forEach(function(video){ video.pause(); });
    }, false, bag);
    watchIntersection(root, function(entries){
      if (!entries[0] || !entries[0].isIntersecting) videos.forEach(function(video){ video.pause(); });
    }, bag, { threshold: 0.05 });
    select(buttons.findIndex ? buttons.findIndex(function(button){ return button.getAttribute('aria-selected') === 'true'; }) : 0, false);

    cleanups.set(root, function(){
      bag.forEach(function(cleanup){ cleanup(); });
      videos.forEach(function(video){ video.pause(); });
      root.removeAttribute('data-kiki-arrival-ready');
    });
  }

  function initialiseJourney(root) {
    if (!root || root.dataset.kikiJourneyReady === 'true') return;
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-jf-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-jf-panel]'));
    if (!tabs.length || !panels.length) return;
    var bag = [];
    var mobile = watchMedia('(max-width: 749px)', sync, bag);
    var current = 0;
    root.dataset.kikiJourneyReady = 'true';

    tabs.forEach(function(tab, i){
      tab.id = tab.id || ('journey-tab-' + i);
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', 'journey-panel-' + i);
      tab.removeAttribute('aria-pressed');
    });
    panels.forEach(function(panel, i){
      panel.id = panel.id || ('journey-panel-' + i);
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tabs[i] ? tabs[i].id : '');
    });

    function sync() {
      tabs.forEach(function(tab, i){
        var active = i === current;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
      });
      panels.forEach(function(panel, i){
        var active = i === current;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', mobile.matches && !active ? 'true' : 'false');
        if ('inert' in panel) panel.inert = mobile.matches && !active;
      });
    }

    function select(i, shouldFocus) {
      current = Math.max(0, Math.min(i, tabs.length - 1));
      sync();
      if (shouldFocus) tabs[current].focus();
    }

    tabs.forEach(function(tab, i){
      listen(tab, 'click', function(){ select(i, false); }, false, bag);
      listen(tab, 'keydown', function(event){
        var target = i;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') target = (i + 1) % tabs.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') target = (i - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = tabs.length - 1;
        else return;
        event.preventDefault();
        select(target, true);
      }, false, bag);
    });
    sync();

    cleanups.set(root, function(){
      bag.forEach(function(cleanup){ cleanup(); });
      root.removeAttribute('data-kiki-journey-ready');
    });
  }

  function initialiseBoundaries(root) {
    if (!root || root.dataset.kikiBoundariesReady === 'true') return;
    var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-cb]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-cb-panel]'));
    if (!buttons.length || !panels.length) return;
    var bag = [];
    var current = 0;
    root.dataset.kikiBoundariesReady = 'true';

    var rail = root.querySelector('.cb-rail');
    if (rail) {
      rail.setAttribute('role', 'tablist');
      rail.setAttribute('aria-label', 'Care boundaries');
    }
    buttons.forEach(function(button, i){
      button.id = button.id || ('care-boundary-tab-' + i);
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', 'care-boundary-panel-' + i);
      button.removeAttribute('aria-pressed');
    });
    panels.forEach(function(panel, i){
      panel.id = panel.id || ('care-boundary-panel-' + i);
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', buttons[i] ? buttons[i].id : '');
    });

    function select(i, shouldFocus) {
      current = Math.max(0, Math.min(i, buttons.length - 1));
      buttons.forEach(function(button, index){
        var active = index === current;
        button.setAttribute('aria-selected', active ? 'true' : 'false');
        button.tabIndex = active ? 0 : -1;
      });
      panels.forEach(function(panel, index){
        var active = index === current;
        panel.hidden = !active;
        panel.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      if (shouldFocus) buttons[current].focus();
    }

    buttons.forEach(function(button, i){
      listen(button, 'click', function(){ select(i, false); }, false, bag);
      listen(button, 'keydown', function(event){
        var target = i;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') target = (i + 1) % buttons.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') target = (i - 1 + buttons.length) % buttons.length;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = buttons.length - 1;
        else return;
        event.preventDefault();
        select(target, true);
      }, false, bag);
    });
    select(buttons.findIndex ? buttons.findIndex(function(button){ return button.getAttribute('aria-selected') === 'true'; }) : 0, false);

    cleanups.set(root, function(){
      bag.forEach(function(cleanup){ cleanup(); });
      root.removeAttribute('data-kiki-boundaries-ready');
    });
  }

  function initialiseScope(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var selectors = [
      ['[data-care-gallery]', initialiseGallery],
      ['[data-arrival-ritual]', initialiseArrival],
      ['[data-journey-finder]', initialiseJourney],
      ['[data-care-boundaries]', initialiseBoundaries]
    ];
    selectors.forEach(function(entry){
      var selector = entry[0];
      var initialise = entry[1];
      if (context.matches && context.matches(selector)) initialise(context);
      Array.prototype.slice.call(context.querySelectorAll(selector)).forEach(initialise);
    });
  }

  function destroyScope(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var selectors = ['[data-care-gallery]','[data-arrival-ritual]','[data-journey-finder]','[data-care-boundaries]'];
    selectors.forEach(function(selector){
      var roots = [];
      if (context.matches && context.matches(selector)) roots.push(context);
      Array.prototype.slice.call(context.querySelectorAll(selector)).forEach(function(root){ roots.push(root); });
      roots.forEach(function(root){
        var cleanup = cleanups.get(root);
        if (cleanup) cleanup();
      });
    });
  }

  function ready() {
    initialiseScope(document);
    document.addEventListener('shopify:section:load', function(event){ initialiseScope(event.target); });
    document.addEventListener('shopify:section:unload', function(event){ destroyScope(event.target); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true });
  else ready();
})();

